import { ApplicationIdFromString, OrganizationIdFromString } from "@superwall/drizzle/branded";
import { Schema } from "effect";
import { encodeBase64 } from "effect/Encoding";
import { StatisticsValueFromProperties } from "./statistics";
import { addEncodedIdToStruct } from "./utils";
const CursorSchema = Schema.Struct({
    id: Schema.Number,
    archived: Schema.Boolean
});
const CursorUrlParams = Schema.compose(Schema.StringFromBase64Url, Schema.parseJson(CursorSchema));
export const UrlParams = Schema.Struct({
    limit: Schema.NumberFromString.pipe(Schema.optionalWith({
        default: ()=>100
    })),
    cursor: CursorUrlParams.pipe(Schema.optional),
    archived: Schema.BooleanFromString.pipe(Schema.optionalWith({
        default: ()=>false
    }))
});
export const Pagination = Schema.Union(Schema.Struct({
    hasMore: Schema.Literal(true),
    cursor: CursorUrlParams
}), Schema.Struct({
    hasMore: Schema.Literal(false),
    cursor: Schema.Null
}));
const NoneWebPaywallPlatform = Schema.Literal("IOS", "ANDROID", "UNKNOWN", "PROMOTIONAL");
const WebPaywallPlatform = Schema.Literal("STRIPE", "PADDLE");
const ApplicationPlatform = Schema.Union(NoneWebPaywallPlatform, WebPaywallPlatform);
const ApplicationPlatformAsProperties = Schema.Struct({
    ios: Schema.Literal(true).pipe(Schema.optional),
    android: Schema.Literal(true).pipe(Schema.optional),
    stripe: Schema.Literal(true).pipe(Schema.optional),
    paddle: Schema.Literal(true).pipe(Schema.optional),
    unknown: Schema.Literal(true).pipe(Schema.optional),
    promotional: Schema.Literal(true).pipe(Schema.optional)
});
export const ApplicationPlatformFromProperties = Schema.transform(ApplicationPlatformAsProperties, ApplicationPlatform, {
    encode: (value)=>{
        const result = {};
        result[value.toLowerCase()] = true;
        return result;
    },
    decode: (toEncode)=>{
        const [key, value] = Object.entries(toEncode)[0];
        return key.toUpperCase();
    },
    strict: true
});
export class Model extends Schema.Class("@superwall/api/Application/Model")({
    object: Schema.Literal("application"),
    id: ApplicationIdFromString,
    organizationId: OrganizationIdFromString,
    platform: ApplicationPlatformFromProperties,
    name: Schema.String.pipe(Schema.NullOr),
    iconUrl: Schema.String,
    createdAt: Schema.DateFromString,
    appId: Schema.String.pipe(Schema.NullishOr),
    projectId: Schema.String.pipe(Schema.NullishOr)
}) {
}
export const Create = Model.pipe(Schema.omit("createdAt", "object", "id", "platform", "projectId"), Schema.extend(Schema.Union(Schema.Struct({
    platform: NoneWebPaywallPlatform,
    marketingMetadata: Schema.Record({
        key: Schema.String,
        value: Schema.Unknown
    }).pipe(Schema.optional)
}), Schema.Struct({
    platform: WebPaywallPlatform,
    domain: Schema.String,
    marketingMetadata: Schema.Record({
        key: Schema.String,
        value: Schema.Unknown
    }).pipe(Schema.optional)
}))));
export class List extends Schema.Class("@superwall/api/Application/List")({
    object: Schema.Literal("list"),
    data: Schema.Array(Model.pipe(Schema.omit("appId", "projectId"))),
    pagination: Pagination
}) {
}
function parseCustomDate(str) {
    // normalise the weird space
    str = str.replace(/\u202F/g, " ").trim();
    // YYYY-MM-DDTh:mm:ss AM|PM
    const m = str.match(/^(\d{4}-\d{2}-\d{2})T(\d{1,2}):(\d{2}):(\d{2})\s?(AM|PM)$/i);
    if (!m) throw new Error("Bad format");
    let [, date, h, min, sec, ampm] = m;
    let hours = +h // Convert string to number
    ;
    if (ampm.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (ampm.toUpperCase() === "AM" && hours === 12) hours = 0;
    // build a proper ISO string; add Z if the time is meant to be UTC
    const iso = `${date}T${hours.toString().padStart(2, "0")}:${min}:${sec}Z`;
    return new Date(iso);
}
// console.log(parseCustomDate('2025-05-09T2:59:59 AM').toISOString());
// // → 2025-05-09T02:59:59.000Z
const DateishStringsWithoutZ = Schema.transform(Schema.String, Schema.String, {
    decode: (from)=>{
        const dateSting = from.replace("Z", "");
        const dateStringParsed = new Date(dateSting);
        if (isNaN(dateStringParsed.getTime())) {
            return parseCustomDate(from).toISOString().replace("Z", "");
        }
        return dateSting;
    },
    encode: (to)=>to,
    strict: true
});
export const StatisticsParams = Schema.Struct({
    environment: Schema.Literal("PRODUCTION", "SANDBOX"),
    // Schema.Array(Schema.Literal("PRODUCTION", "SANDBOX")),
    organizationId: Schema.NumberFromString,
    // applicationId: Schema.Array(Schema.NumberFromString),
    from: DateishStringsWithoutZ,
    to: DateishStringsWithoutZ
});
export class Statistics extends Schema.Class("@superwall/api/Application/Statistics")({
    object: Schema.Literal("statistics"),
    params: Schema.Struct({
        applicationId: Schema.Array(Schema.NumberFromString),
        environment: Schema.Array(Schema.Literal("PRODUCTION", "SANDBOX")),
        // Schema.Array(Schema.Literal("PRODUCTION", "SANDBOX")),
        organizationId: Schema.NumberFromString,
        // applicationId: Schema.Array(Schema.NumberFromString),
        from: Schema.String,
        to: Schema.String
    }),
    statistics: Schema.Array(Schema.Struct({
        key: Schema.String,
        chart: Schema.String,
        name: Schema.String,
        value: StatisticsValueFromProperties,
        description: Schema.String
    }).pipe((s)=>addEncodedIdToStruct(s, (value)=>encodeBase64(`${value.key}-${value.chart}-${value.name}`))))
}) {
}
export class RecentTransaction extends Schema.Class("@superwall/api/Application/RecentTransaction")({
    id: Schema.String,
    isProcessing: Schema.Boolean,
    object: Schema.Literal("recent_transaction"),
    placement: Schema.String.pipe(Schema.NullOr),
    type: Schema.String,
    store: Schema.String.pipe(Schema.NullOr),
    price: Schema.Number.pipe(Schema.NullOr),
    purchasedAt: Schema.DateFromString,
    user: Schema.Struct({
        object: Schema.Literal("recent_transaction.user"),
        appUserId: Schema.String
    }).pipe(Schema.NullOr),
    integration: Schema.String,
    paywall: Schema.Struct({
        object: Schema.Literal("recent_transaction.paywall"),
        name: Schema.String
    }).pipe(Schema.NullOr)
}) {
}
export class RecentTransactionList extends Schema.Class("@superwall/api/Application/RecentTransactionList")({
    object: Schema.Literal("list"),
    data: Schema.Array(RecentTransaction)
}) {
}
